<template>
  <div class="page-header align-items-start min-vh-100"
    style="
      background-image: url('https://images.unsplash.com/photo-1497294815431-9365093b7331?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1950&q=80');">
    <span class="mask bg-gradient-dark opacity-6"></span>
    <div class="container my-auto">
      <div class="row">
        <div class="col-lg-4 col-md-8 col-12 mx-auto">
          <div class="card z-index-0 fadeIn3 fadeInBottom">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="bg-gradient-success shadow-success border-radius-lg py-3 pe-1">
                <h4 class="text-white font-weight-bolder text-center mt-2 mb-0">
                  ChurchScheduling.com Sign in
                </h4>
              </div>
            </div>
            <div class="card-body">
              <!-- Password Reset Required Banner -->
              <div v-if="passwordResetMessage" class="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mb-3 mt-3" role="alert">
                <i class="material-icons text-warning" style="font-size: 20px; margin-top: 2px;">info</i>
                <div>
                  <p class="text-sm mb-2" style="color: #664d03;">{{ passwordResetMessage }}</p>
                  <span class="text-xs" style="color: #664d03;">Redirecting to password reset...</span>
                </div>
              </div>

              <!-- Failed Attempts Warning -->
              <div v-if="showFailedAttemptsWarning" class="d-flex align-items-start gap-2 py-3 px-3 mb-3 mt-3 rounded" role="alert"
                style="background-color: #fff3cd; border: 1px solid #ffecb5;">
                <i class="material-icons" style="font-size: 20px; margin-top: 2px; color: #997404;">error_outline</i>
                <div>
                  <p class="text-sm fw-semibold mb-1" style="color: #664d03;">Your login has failed multiple times.</p>
                  <p class="text-sm mb-2" style="color: #664d03;">Please consider using the password reset option to generate a new password.</p>
                  <button type="button" @click="redirectToForgetPassword"
                    class="btn btn-sm mb-0"
                    style="font-size: 0.75rem; background-color: #664d03; color: #fff; border: none;">
                    Reset Password
                  </button>
                </div>
              </div>

              <form role="form" class="text-start mt-3" @submit.prevent="handleLogin">
                <div class="mb-3">
                  <MaterialInput id="email" type="email" label="Email" v-model="email" name="email"
                    :error="!!emailError" @blur="validateEmail" />
                  <small v-if="emailError" class="text-danger">{{ emailError }}</small>
                </div>
                <div class="mb-3">
                  <MaterialInput id="password" type="password" label="password" v-model="password" name="password" />
                </div>

                <!-- hCaptcha (rendered explicitly from onMounted) -->
                <div class="mb-3 d-flex justify-content-center">
                  <div ref="hcaptchaSignin"></div>
                </div>
                <small v-if="captchaError" class="text-danger d-block text-center mb-2">{{ captchaError }}</small>

                <div class="text-center">
                  <MaterialButton class="my-4 mb-2" variant="gradient" color="success" fullWidth :disabled="loading || !!passwordResetMessage">
                    <span v-if="!loading">Sign in</span>
                    <span v-else>Signing in...</span>
                  </MaterialButton>
                </div>

                <p v-if="error && !passwordResetMessage && !showFailedAttemptsWarning" class="text-danger text-center">
                  {{ error }}
                </p>
                <button type="button" @click="redirectToForgetPassword"
                  class="text-center text-sm text-success text-gradient font-weight-bold mx-auto"
                  style="all: unset; display: block; cursor: pointer;">
                  Forgot Password?
                </button>
                <p class="mt-4 text-sm text-center">
                  Don't have an account?
                  <router-link :to="{ name: 'SignUp', query: route.query.redirect ? { redirect: route.query.redirect } : {} }" class="text-success text-gradient font-weight-bold">
                    Sign up
                  </router-link>
                </p>
              </form>
            </div>
          </div>
        </div>
        <p class="text-center mt-4">
          <a href="https://churchscheduling.com" rel="noopener noreferrer"
            style="color: rgba(255,255,255,0.55); font-size: 0.8rem; text-decoration: none;">
            ChurchScheduling.com
          </a>
        </p>
      </div>
    </div>

    <!-- Location Selection Modal -->
    <LocationModal v-if="showLocationModal" :modalHeading="'Select Location'" :dropdownList="locationsList"
      @selectedValue="handleLocationSelection" @close="closeLocationModal" />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeMount, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import LocationModal from "@/components/Modal/LocationModal.vue";
import { getLocalStorage, setLocalStorage } from "@/utils/LocalStorageHandler.js";
import { isValidEmail } from "@/utils/validation";
import {
  renderHcaptcha,
  getHcaptchaResponse,
  resetHcaptcha,
  removeHcaptcha,
} from "@/utils/HcaptchaHelper.js";

const store = useStore();
const router = useRouter();
const route = useRoute();

const email = ref("");
const password = ref("");
const emailError = ref(null);
const captchaError = ref(null);
const failedAttempts = ref(0);
const passwordResetMessage = ref("");
const hcaptchaSitekey = process.env.VUE_APP_HCAPTCHA_SITEKEY || '0454c266-d5fc-443c-8dea-7140009ab354';

const getPostLoginRoute = (fallback) => {
  const redirect = route.query.redirect;
  return typeof redirect === 'string' && (
    /^\/serve-invitation\/[a-f0-9]{64}$/.test(redirect)
    || /^\/people-groups\/invitations\/accept\?token=[A-Za-z0-9]+$/.test(redirect)
  )
    ? redirect
    : fallback;
};

const hcaptchaSignin = ref(null);
let hcaptchaWidgetId = null;

const error = computed(() => store.getters.authError);
const loading = computed(() => store.getters.authLoading);
const showFailedAttemptsWarning = computed(() => failedAttempts.value >= 3 && !passwordResetMessage.value);
const showLocationModal = ref(false);
const locationsList = ref([]);
const stLocations = computed(() => store.getters.stLocations);
const currentPersonId = ref(null);

const validateEmail = () => {
  if (email.value && email.value.trim() !== '') {
    if (!isValidEmail(email.value)) {
      emailError.value = 'Please enter a valid email address';
    } else {
      emailError.value = null;
    }
  } else {
    emailError.value = null;
  }
};

const redirectToForgetPassword = async (firstname) => {
  try {
    await store.dispatch('setEmail', email.value);
    // Only set firstname if it's a real string (not a DOM event from @click)
    if (firstname && typeof firstname === 'string') {
      store.commit('SET_FIRSTNAME', firstname);
    }
    router.push('/forgot-password');
  } catch (e) {
    console.error("Redirect failed", e);
  }
};


const handleLogin = async () => {
  // Validate email before login
  validateEmail();

  if (emailError.value) {
    return;
  }

  // Validate hCaptcha
  if (hcaptchaWidgetId === null) {
    captchaError.value = 'Captcha is still loading. Please wait a moment and try again, or refresh the page if it never appears (ad blockers can prevent it from loading).';
    return;
  }
  const hcaptchaResponse = getHcaptchaResponse(hcaptchaWidgetId);
  if (!hcaptchaResponse) {
    captchaError.value = 'Please complete the captcha verification';
    return;
  }
  captchaError.value = null;

  try {
    const response = await store.dispatch("login", { email: email.value, password: password.value, h_captcha_response: hcaptchaResponse });
    const responseLocationId = response?.output?.session_details?.locationid;
    const personId = response?.output?.user?.personid;
    currentPersonId.value = personId;

    // await store.dispatch("fetchSidebarRoutes", { locationid: responseLocationId });
    // if (responseLocationId) {
    //   await store.dispatch("fetchDashboardData", { personid: personId, locationid: responseLocationId });
    //   await store.dispatch("fetchPermissionData", { locationid: responseLocationId });
    // }

    if (response?.output?.session_details?.hasMultipleLocations) {
      let sessionData = getLocalStorage("sessionData") || {};
      if (sessionData.locationid) {
        delete sessionData.locationid;
        setLocalStorage("sessionData", sessionData);
        store.commit("SET_SESSION_DATA", sessionData);
      }
      await store.dispatch("fetchPersonLocations", { personid: personId });
      locationsList.value = stLocations.value || [];
      showLocationModal.value = true;

    } else {
      // Single-location user: set the location in the backend session
      // (same as handleLocationSelection does for multi-location users)
      if (responseLocationId) {
        await store.dispatch("setPersonLocations", responseLocationId);

        // Ensure sessionData in localStorage has the locationid
        let sessionData = getLocalStorage("sessionData") || {};
        sessionData = {
          ...sessionData,
          locationid: String(responseLocationId),
        };
        setLocalStorage("sessionData", sessionData);
        store.commit("SET_SESSION_DATA", sessionData);
      }

      await store.dispatch("fetchSidebarRoutes", { locationid: responseLocationId });
      if (responseLocationId) {
        await store.dispatch("fetchDashboardData", { personid: personId, locationid: responseLocationId });
        await store.dispatch("fetchPermissionData", { locationid: responseLocationId });
      }
      const defaultRoute = store.getters.stSidebarRoutes?.defaultRoute;
      router.push(getPostLoginRoute(defaultRoute ? `/${defaultRoute}` : "/"));
    }
  } catch (e) {
    const responseData = e.response?.data;

    // Handle legacy user who needs to reset password (password_new is null)
    if (responseData?.requires_password_reset) {
      const userFirstname = responseData.output?.user?.firstname || '';
      passwordResetMessage.value = responseData.message || 'It looks like this is your first time signing in. Please reset your password to continue.';
      // Auto-redirect to password reset after a short delay
      setTimeout(() => {
        redirectToForgetPassword(userFirstname);
      }, 3000);
      return;
    }

    // Track failed login attempts
    failedAttempts.value++;
    // Reset the captcha so the user can try again without reloading.
    resetHcaptcha(hcaptchaWidgetId);
    console.error("Login failed", e);
  }
};

const handleLocationSelection = async (locationId) => {

  if (!locationId) {
    console.log("No locationId provided, closing modal");
    showLocationModal.value = false;
    return;
  }

  try {
    await store.dispatch("setPersonLocations", locationId);

    let organizationid = null;
    const locations = stLocations.value;

    if (locations) {
      const locationList = Array.isArray(locations) ? locations : [locations];
      const foundOrg = locationList.find(org =>
        org.locations?.some(loc => String(loc.location_id) === String(locationId))
      );
      if (foundOrg && foundOrg.organization_id) {
        organizationid = foundOrg.organization_id;
      }
    }

    // Ensure locationId is a string (for consistency with dropdown comparison)
    const locationIdString = String(locationId);

    // Update sessionData in localStorage (same as App.vue)
    let sessionData = getLocalStorage("sessionData") || {};
    sessionData = {
      ...sessionData,
      locationid: locationIdString,
      organizationid: organizationid || sessionData.organizationid || null
    };
    setLocalStorage("sessionData", sessionData);

    // Verify location was saved to localStorage
    const savedLocationId = getLocalStorage("sessionData")?.locationid;
    if (savedLocationId !== locationIdString) {
      console.error("Failed to save location to localStorage");
    } else {
      console.log("Location saved to localStorage:", locationIdString);
    }
    store.commit("SET_SESSION_DATA", sessionData);

    // Wait for next tick to ensure all updates are processed
    await nextTick();

    // Close modal first
    showLocationModal.value = false;

    await store.dispatch("fetchSidebarRoutes", { locationid: locationIdString });
    if (currentPersonId.value) {
      await store.dispatch("fetchDashboardData", {
        personid: currentPersonId.value,
        locationid: locationIdString,
      });
    }
    await store.dispatch("fetchPermissionData", { locationid: locationIdString });

    // Reload the page so App.vue can load dashboard data with the selected location
    // This ensures sidebar and all APIs are called after location is selected
    // window.location.reload();
    // router.push("/");
    const defaultRoute = store.getters.stSidebarRoutes?.defaultRoute;
    router.push(getPostLoginRoute(defaultRoute ? `/${defaultRoute}` : "/sign-in"));
  } catch (e) {
    console.error("Location selection failed", e);
  }
};

const closeLocationModal = () => {
  console.log("closeLocationModal called");
  showLocationModal.value = false;
};

// Watch for locations changes (same as App.vue)
watch(stLocations, (newVal) => {
  if (newVal) {
    locationsList.value = newVal;
  }
});

onBeforeMount(() => {
  store.commit("toggleEveryDisplay");
  store.commit("toggleHideConfig");
  store.commit("SET_ERROR", null);
});

onMounted(async () => {
  try {
    hcaptchaWidgetId = await renderHcaptcha(hcaptchaSignin.value, hcaptchaSitekey, {
      callback: () => {
        captchaError.value = null;
      },
      'expired-callback': () => {
        captchaError.value = 'Captcha expired. Please solve it again.';
      },
      'error-callback': () => {
        captchaError.value = 'Captcha failed to load. Please refresh and try again.';
      },
    });
  } catch (e) {
    console.error('Failed to render hCaptcha on sign-in:', e);
    captchaError.value = 'Captcha failed to load. Please disable ad blockers or refresh the page.';
  }
});

onBeforeUnmount(() => {
  removeHcaptcha(hcaptchaWidgetId);
  hcaptchaWidgetId = null;
  store.commit("toggleEveryDisplay");
  store.commit("toggleHideConfig");
  store.commit("SET_ERROR", null);
});
</script>
